// pages/SalesEngagement.jsx
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Phone,
  Mail,
  Calendar,
  Clock,
  User,
  Building2,
  CheckCircle,
  XCircle,
  PhoneOff,
  PhoneMissed,
  PhoneForwarded,
  AlertCircle,
  TrendingUp,
  BarChart3,
  RefreshCw,
  Filter,
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Eye,
  Send,
  CalendarDays,
  CalendarClock,
  CalendarRange,
  CalendarCheck,
  X,
} from "lucide-react";
import apiService from "../../services/api";
import LogCallModal from "../LogCallModal";
import AdvancedLeadFilterModal from "../../components/AdvancedLeadFilterModal";
import { useRealtimeSync } from "../../hooks/useRealtimeSync";
import { useTheme } from "../../contexts/ThemeContext";
import PhoneLink from "../../components/PhoneLink";

const SalesEngagement = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const searchTimeoutRef = useRef(null);
  const hasRestoredScroll = useRef(false);
  const isInitialMount = useRef(true);

  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDateRange, setSelectedDateRange] = useState(() => {
    const saved = sessionStorage.getItem("salesEngagementDateRange");
    return saved ? JSON.parse(saved) : "today";
  });
  const [selectedStep, setSelectedStep] = useState(() => {
    const saved = sessionStorage.getItem("salesEngagementStep");
    return saved ? JSON.parse(saved) : "all";
  });
  const [stepCounts, setStepCounts] = useState({});
  const [totalInDateRange, setTotalInDateRange] = useState(0);
  const [searchTerm, setSearchTerm] = useState(() => {
    const saved = sessionStorage.getItem("salesEngagementSearch");
    return saved ? JSON.parse(saved) : "";
  });
  const [debouncedSearch, setDebouncedSearch] = useState(() => {
    const saved = sessionStorage.getItem("salesEngagementSearch");
    return saved ? JSON.parse(saved) : "";
  });
  const [showLogCallModal, setShowLogCallModal] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [stats, setStats] = useState(null);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  const [advancedFilterConfig, setAdvancedFilterConfig] = useState(() => {
    const saved = sessionStorage.getItem("salesEngagementAdvancedFilters");
    return saved ? JSON.parse(saved) : null;
  });

  const [selectedFilterPresetId, setSelectedFilterPresetId] = useState(() => {
    const saved = sessionStorage.getItem("salesEngagementActivePresetId");
    return saved ? JSON.parse(saved) : null;
  });

  const [pagination, setPagination] = useState(() => {
    const saved = sessionStorage.getItem("salesEngagementPagination");
    return saved
      ? JSON.parse(saved)
      : { total: 0, page: 1, limit: 50, pages: 0 };
  });

  const hasAdvancedFilters = useMemo(() => {
    return (
      advancedFilterConfig &&
      advancedFilterConfig.conditions &&
      advancedFilterConfig.conditions.length > 0
    );
  }, [advancedFilterConfig]);

  const isAdvancedFilterMode = useMemo(() => {
    return hasAdvancedFilters || !!selectedFilterPresetId;
  }, [hasAdvancedFilters, selectedFilterPresetId]);

  const dateRanges = [
    {
      key: "today",
      label: "Today",
      icon: CalendarDays,
      description: "Leads created today",
    },
    {
      key: "yesterday",
      label: "Yesterday",
      icon: CalendarClock,
      description: "Leads created yesterday",
    },
    {
      key: "last_week",
      label: "Last 7 Days",
      icon: CalendarRange,
      description: "Leads from the past week",
    },
    {
      key: "last_month",
      label: "Last 30 Days",
      icon: CalendarCheck,
      description: "Leads from the past month",
    },
    {
      key: "all_time",
      label: "All Time",
      icon: CalendarCheck,
      description: "All Time Leads",
    },
  ];

  const steps = [
    { key: "all", label: "All Steps", color: "blue" },
    { key: "not_contacted", label: "Not Contacted", color: "gray" },
    { key: "email_sent", label: "Email Sent", color: "cyan" },
    { key: "call_1", label: "Call 1", color: "yellow" },
    { key: "call_2", label: "Call 2", color: "orange" },
    { key: "call_3", label: "Call 3", color: "purple" },
    { key: "call_4", label: "Call 4", color: "indigo" },
    { key: "unqualified", label: "Unqualified", color: "red" },
    { key: "converted", label: "Converted", color: "green" },
    { key: "call_back_later", label: "Callback", color: "teal" },
  ];

  useEffect(() => {
    const handleBeforeUnload = () => {
      sessionStorage.setItem(
        "salesEngagementScrollPosition",
        window.scrollY.toString(),
      );
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      sessionStorage.setItem(
        "salesEngagementScrollPosition",
        window.scrollY.toString(),
      );
    };
  }, []);

  useEffect(() => {
    if (!loading && !hasRestoredScroll.current && leads.length > 0) {
      const savedScrollPosition = sessionStorage.getItem(
        "salesEngagementScrollPosition",
      );
      if (savedScrollPosition) {
        const timer = setTimeout(() => {
          window.scrollTo(0, parseInt(savedScrollPosition, 10));
          hasRestoredScroll.current = true;
        }, 50);
        return () => clearTimeout(timer);
      }
    }
  }, [loading, leads]);

  useEffect(() => {
    sessionStorage.setItem(
      "salesEngagementDateRange",
      JSON.stringify(selectedDateRange),
    );
  }, [selectedDateRange]);

  useEffect(() => {
    sessionStorage.setItem("salesEngagementStep", JSON.stringify(selectedStep));
  }, [selectedStep]);

  useEffect(() => {
    sessionStorage.setItem("salesEngagementSearch", JSON.stringify(searchTerm));
  }, [searchTerm]);

  useEffect(() => {
    sessionStorage.setItem(
      "salesEngagementPagination",
      JSON.stringify(pagination),
    );
  }, [pagination]);

  useEffect(() => {
    if (advancedFilterConfig) {
      sessionStorage.setItem(
        "salesEngagementAdvancedFilters",
        JSON.stringify(advancedFilterConfig),
      );
    } else {
      sessionStorage.removeItem("salesEngagementAdvancedFilters");
    }
  }, [advancedFilterConfig]);

  useEffect(() => {
    if (selectedFilterPresetId) {
      sessionStorage.setItem(
        "salesEngagementActivePresetId",
        JSON.stringify(selectedFilterPresetId),
      );
    } else {
      sessionStorage.removeItem("salesEngagementActivePresetId");
    }
  }, [selectedFilterPresetId]);

  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(
      () => setDebouncedSearch(searchTerm),
      400,
    );
    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchTerm]);

  const handleRealtimeUpdate = useCallback((data) => {
    if (data.type === "sales_engagement_updated") {
      const { action, data: leadData } = data;
      setLeads((prevLeads) => {
        if (action === "created") return [leadData, ...prevLeads];
        else if (action === "updated" || action === "call_logged")
          return prevLeads.map((lead) =>
            lead.id === leadData.id ? { ...lead, ...leadData } : lead,
          );
        return prevLeads;
      });
      fetchStats();
    }
  }, []);

  const { isConnected } = useRealtimeSync({
    events: ["sales_engagement_updated"],
    onUpdate: handleRealtimeUpdate,
    enabled: true,
  });

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchLeads();
  }, [
    isAdvancedFilterMode,
    selectedDateRange,
    selectedStep,
    debouncedSearch,
    pagination.page,
    advancedFilterConfig,
    selectedFilterPresetId,
  ]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setPagination((prev) => (prev.page !== 1 ? { ...prev, page: 1 } : prev));
  }, [
    selectedDateRange,
    selectedStep,
    debouncedSearch,
    JSON.stringify(advancedFilterConfig),
    selectedFilterPresetId,
  ]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const params = {
        search: debouncedSearch || undefined,
        page: pagination.page,
        limit: pagination.limit,
      };
      if (!isAdvancedFilterMode) {
        params.dateRange = selectedDateRange;
        params.step = selectedStep === "all" ? undefined : selectedStep;
      }
      if (hasAdvancedFilters)
        params.advancedFilters = JSON.stringify(advancedFilterConfig);
      if (selectedFilterPresetId)
        params.filterPresetId = selectedFilterPresetId;

      const response = isAdvancedFilterMode
        ? await apiService.getSalesEngagementLeadsAdvanced(params)
        : await apiService.getSalesEngagementLeads(params);

      setLeads(response.data);
      setStepCounts(response.stepCounts);
      setTotalInDateRange(response.totalInDateRange || 0);
      if (response.pagination) {
        setPagination((prev) => ({
          ...prev,
          total: response.pagination.total,
          pages: response.pagination.pages,
        }));
      }
    } catch (error) {
      console.error("❌ Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await apiService.getSalesEngagementStats();
      setStats(response.data);
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  const handleLogCall = (lead) => {
    setSelectedLead(lead);
    setShowLogCallModal(true);
  };

  const handleDateRangeChange = (range) => {
    setSelectedDateRange(range);
    setSelectedStep("all");
    hasRestoredScroll.current = false;
  };

  const handleStepChange = (step) => {
    setSelectedStep(step);
    hasRestoredScroll.current = false;
  };

  const handleClearAdvancedFilters = () => {
    setAdvancedFilterConfig(null);
    setSelectedFilterPresetId(null);
    setPagination((prev) => ({ ...prev, page: 1 }));
    hasRestoredScroll.current = false;
  };

  const getStepColor = (step) =>
    steps.find((s) => s.key === step)?.color || "gray";

  const getStepBadgeClass = (step) => {
    const colors = {
      gray: "bg-gray-500/10 text-gray-400 border-gray-500/20",
      cyan: "bg-cyan-500/10 text-cyan-400 border-cyan-500/20",
      yellow: "bg-yellow-500/10 text-yellow-400 border-yellow-500/20",
      orange: "bg-orange-500/10 text-orange-400 border-orange-500/20",
      red: "bg-red-500/10 text-red-400 border-red-500/20",
      purple: "bg-purple-500/10 text-purple-400 border-purple-500/20",
      green: "bg-green-500/10 text-green-400 border-green-500/20",
      indigo: "bg-indigo-500/10 text-indigo-400 border-indigo-500/20",
      blue: "bg-blue-500/10 text-blue-400 border-blue-500/20",
      teal: "bg-teal-500/10 text-teal-400 border-teal-500/20",
    };
    return colors[getStepColor(step)] || colors.gray;
  };

  const getStepLabel = (step) =>
    steps.find((s) => s.key === step)?.label || step;

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const DISQUALIFICATION_LABELS = {
    not_interested: "Not Interested",
    budget: "Budget Constraints",
    already_have_solution: "Already Have Solution",
    wrong_contact: "Wrong Contact",
    no_authority: "No Decision Authority",
    timing_not_right: "Timing Not Right",
    contest_lead: "Contested",
    other: "Other",
  };

  const formatDisqualificationReason = (reason) =>
    DISQUALIFICATION_LABELS[reason] ?? reason;

  // Shared class helpers
  const headerBg = isDarkMode
    ? "bg-gradient-to-r from-[#0C0E12] via-[#0E1015] to-[#0C0E12] border-gray-800/50"
    : "bg-gradient-to-r from-gray-50 via-white to-gray-50 border-gray-200";

  const tableBg = isDarkMode
    ? "bg-[#13161B] border-gray-700/50"
    : "bg-white border-gray-200";

  const theadBg = isDarkMode
    ? "bg-gray-800/50 border-gray-700/50"
    : "bg-gray-50 border-gray-200";
  const thText = isDarkMode ? "text-gray-400" : "text-gray-500";
  const divideColor = isDarkMode ? "divide-gray-700/50" : "divide-gray-200";
  const mutedText = isDarkMode ? "text-gray-400" : "text-gray-500";
  const primaryText = isDarkMode ? "text-white" : "text-gray-900";
  const secondaryText = isDarkMode ? "text-gray-300" : "text-gray-600";

  const dateTabActive =
    "bg-blue-600/20 border-blue-500/40 text-blue-300 shadow-lg shadow-blue-500/10";
  const dateTabInactive = isDarkMode
    ? "bg-gray-800/30 border-gray-700/50 text-gray-400 hover:bg-gray-700/40 hover:text-gray-300"
    : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700";

  const stepTabInactive = isDarkMode
    ? "bg-gray-800/30 border-gray-700/50 text-gray-400 hover:bg-gray-700/50"
    : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50";

  const refreshBtn = isDarkMode
    ? "bg-gray-800/50 hover:bg-gray-700/50 border-gray-700/50 text-white"
    : "bg-white hover:bg-gray-50 border-gray-300 text-gray-700";

  const advFilterBtn = isDarkMode
    ? "bg-gray-800/50 hover:bg-gray-700/50 border-gray-700/50 text-white"
    : "bg-white hover:bg-gray-50 border-gray-300 text-gray-700";

  const searchInputCls = isDarkMode
    ? "bg-gray-900/50 border-gray-700/50 text-white placeholder-gray-500 focus:border-blue-500/50 focus:ring-blue-500/20"
    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:border-blue-400 focus:ring-blue-400/20";

  const paginateBtnCls = isDarkMode
    ? "bg-gray-800/50 border-gray-700/50 text-gray-300 hover:bg-gray-700/50"
    : "bg-white border-gray-300 text-gray-600 hover:bg-gray-50";

  return (
    <div
      className={`min-h-screen ${isDarkMode ? "bg-[#0C0E12]" : "bg-gray-50"}`}
    >
      {/* Header */}
      <div className={`border-b ${headerBg}`}>
        <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2 flex-wrap">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <h1 className={`text-xl sm:text-3xl font-bold ${primaryText}`}>
                  Sales Engagement
                </h1>
                {isConnected && (
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-green-500/10 border border-green-500/20 rounded-full text-xs text-green-400">
                    <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                    Live
                  </span>
                )}
              </div>
              <p className={`hidden sm:block ${mutedText}`}>
                Manage MVF leads through the engagement workflow
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap justify-end">
              <button
                onClick={() => {
                  fetchLeads();
                  fetchStats();
                }}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 border rounded-lg transition-all duration-200 ${refreshBtn}`}
              >
                <RefreshCw
                  size={18}
                  className={loading ? "animate-spin" : ""}
                />
                <span className="hidden sm:inline">Refresh</span>
              </button>

              {isAdvancedFilterMode && (
                <button
                  onClick={handleClearAdvancedFilters}
                  className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-all duration-200"
                >
                  <X size={18} />
                  <span className="hidden sm:inline">Clear Filters</span>
                </button>
              )}

              <button
                onClick={() => setShowAdvancedFilters(true)}
                className={`flex items-center gap-2 px-3 sm:px-4 py-2 rounded-lg transition-all duration-200 ${
                  isAdvancedFilterMode
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : `border ${advFilterBtn}`
                }`}
              >
                <Filter size={18} />
                <span className="hidden sm:inline">Advanced Filters</span>
                {isAdvancedFilterMode && (
                  <span className="px-2 py-0.5 bg-white/20 rounded-full text-xs hidden sm:inline">
                    Active
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Stats Overview */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
              <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border border-blue-500/20 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm ${mutedText}`}>
                    Total Leads (30d)
                  </span>
                  <TrendingUp className="text-blue-400" size={20} />
                </div>
                <div className={`text-2xl font-bold ${primaryText}`}>
                  {stats.conversionRates.total}
                </div>
              </div>

              <div className="bg-gradient-to-br from-green-500/10 to-green-600/5 border border-green-500/20 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm ${mutedText}`}>Qualified</span>
                  <CheckCircle className="text-green-400" size={20} />
                </div>
                <div className={`text-2xl font-bold ${primaryText}`}>
                  {stats.conversionRates.qualified}
                  <span className={`text-sm ml-2 ${mutedText}`}>
                    ({stats.conversionRates.qualificationRate}%)
                  </span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border border-orange-500/20 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm ${mutedText}`}>Email Opens</span>
                  <Mail className="text-orange-400" size={20} />
                </div>
                <div className={`text-2xl font-bold ${primaryText}`}>
                  {stats.emailStats.opened}
                  <span className={`text-sm ml-2 ${mutedText}`}>
                    / {stats.emailStats.sent}
                  </span>
                </div>
              </div>

              <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border border-purple-500/20 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className={`text-sm ${mutedText}`}>Replies</span>
                  <Send className="text-purple-400" size={20} />
                </div>
                <div className={`text-2xl font-bold ${primaryText}`}>
                  {stats.emailStats.replied}
                </div>
              </div>
            </div>
          )}

          {/* Conditional Filter Display */}
          {isAdvancedFilterMode ? (
            <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Filter className="text-blue-400" size={20} />
                  <div>
                    <div className={`font-medium ${primaryText}`}>
                      Advanced Filters Active
                    </div>
                    <div className={`text-sm ${mutedText}`}>
                      Showing filtered results
                      {selectedFilterPresetId && " from saved preset"}
                    </div>
                  </div>
                </div>
                <div className={`text-sm ${mutedText}`}>
                  {loading ? (
                    "Loading..."
                  ) : (
                    <>
                      <span className={`font-medium ${primaryText}`}>
                        {pagination.total}
                      </span>{" "}
                      lead{pagination.total !== 1 ? "s" : ""} found
                    </>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Date Range Tabs */}
              <div className="flex items-center gap-2 mb-4 overflow-x-auto pb-1 -mx-3 px-3 sm:mx-0 sm:px-0 sm:flex-wrap">
                {dateRanges.map((range) => {
                  const Icon = range.icon;
                  const isActive = selectedDateRange === range.key;
                  return (
                    <button
                      key={range.key}
                      onClick={() => handleDateRangeChange(range.key)}
                      className={`flex items-center gap-2 px-3 sm:px-5 py-2 sm:py-2.5 rounded-xl border transition-all duration-200 whitespace-nowrap flex-shrink-0 ${
                        isActive ? dateTabActive : dateTabInactive
                      }`}
                    >
                      <Icon size={16} />
                      <span className="font-medium text-sm sm:text-base">
                        {range.label}
                      </span>
                    </button>
                  );
                })}
              </div>

              {/* Step Filters */}
              <div className="flex items-center gap-2 overflow-x-auto pb-2">
                {steps.map((step) => {
                  const count =
                    step.key === "all"
                      ? totalInDateRange
                      : stepCounts[step.key] || 0;
                  return (
                    <button
                      key={step.key}
                      onClick={() => handleStepChange(step.key)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all duration-200 whitespace-nowrap ${
                        selectedStep === step.key
                          ? getStepBadgeClass(step.key)
                          : stepTabInactive
                      }`}
                    >
                      <span>{step.label}</span>
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          selectedStep === step.key
                            ? "bg-white/10"
                            : isDarkMode
                              ? "bg-gray-700/50"
                              : "bg-gray-200"
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-4">
        <div className="flex items-center gap-4">
          <div className="relative flex-1">
            <Search
              className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${mutedText}`}
              size={20}
            />
            <input
              type="text"
              placeholder="Search leads by name, email, company, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-12 pr-4 py-3 border rounded-xl focus:outline-none focus:ring-2 ${searchInputCls}`}
            />
          </div>
          {!isAdvancedFilterMode && (
            <div className={`text-sm whitespace-nowrap ${mutedText}`}>
              {loading ? (
                "Loading..."
              ) : (
                <>
                  <span className={`font-medium ${primaryText}`}>
                    {pagination.total}
                  </span>{" "}
                  lead{pagination.total !== 1 ? "s" : ""} found
                </>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Leads Table */}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 pb-6">
        <div className={`rounded-xl border overflow-hidden ${tableBg}`}>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className={`border-b ${theadBg}`}>
                <tr>
                  <th
                    className={`px-4 sm:px-6 py-4 text-xs font-medium uppercase tracking-wider text-left ${thText}`}
                  >
                    Lead
                  </th>
                  <th
                    className={`px-4 sm:px-6 py-4 text-xs font-medium uppercase tracking-wider text-left ${thText} hidden sm:table-cell`}
                  >
                    Contact
                  </th>
                  <th
                    className={`px-4 sm:px-6 py-4 text-xs font-medium uppercase tracking-wider text-left ${thText} hidden md:table-cell`}
                  >
                    Company
                  </th>
                  <th
                    className={`px-4 sm:px-6 py-4 text-xs font-medium uppercase tracking-wider text-left ${thText} hidden lg:table-cell`}
                  >
                    Assigned To
                  </th>
                  <th
                    className={`px-4 sm:px-6 py-4 text-xs font-medium uppercase tracking-wider text-left ${thText} hidden sm:table-cell`}
                  >
                    Calls
                  </th>
                  <th
                    className={`px-4 sm:px-6 py-4 text-xs font-medium uppercase tracking-wider text-right ${thText}`}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${divideColor}`}>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 sm:px-6 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <RefreshCw
                          size={24}
                          className={`animate-spin ${mutedText}`}
                        />
                        <span className={mutedText}>Loading leads...</span>
                      </div>
                    </td>
                  </tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 sm:px-6 py-16 text-center">
                      <div className="flex flex-col items-center gap-3">
                        <AlertCircle
                          size={32}
                          className={
                            isDarkMode ? "text-gray-600" : "text-gray-300"
                          }
                        />
                        <div>
                          <p className={`font-medium ${mutedText}`}>
                            No leads found
                          </p>
                          <p
                            className={`text-sm mt-1 ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                          >
                            {debouncedSearch
                              ? `No results for "${debouncedSearch}"`
                              : isAdvancedFilterMode
                                ? "No leads match the selected filters"
                                : `No leads for ${dateRanges
                                    .find((d) => d.key === selectedDateRange)
                                    ?.label?.toLowerCase()}${selectedStep !== "all" ? ` with step "${getStepLabel(selectedStep)}"` : ""}`}
                          </p>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  leads
                    .filter((lead) => {
                      const isConverted =
                        lead.leadStatus === "converted" ||
                        lead.salesEngagementStep === "qualified";
                      return selectedStep === "converted"
                        ? isConverted
                        : !isConverted;
                    })
                    .map((lead) => (
                      <tr
                        key={lead.id}
                        className={`transition-colors cursor-pointer ${
                          lead.leadStatus === "converted" ||
                          lead.salesEngagementStep === "qualified"
                            ? "bg-green-500/10 border-l-4 border-l-green-500"
                            : lead.leadStatus === "unqualified" ||
                                lead.leadStatus === "dead"
                              ? "bg-red-500/10 border-l-4 border-l-red-500"
                              : isDarkMode
                                ? "hover:bg-gray-800/30"
                                : "hover:bg-gray-50"
                        }`}
                      >
                        <td
                          className="px-4 sm:px-6 py-4"
                          onClick={() => navigate(`/leads/${lead.id}`)}
                        >
                          <div>
                            <div className={`font-medium ${primaryText}`}>
                              {lead.fullName || lead.email}
                            </div>
                            <div className={`text-sm ${mutedText}`}>
                              {lead.email}
                            </div>
                          </div>
                        </td>

                        <td className="px-4 sm:px-6 py-4 hidden sm:table-cell">
                          <div className="flex flex-col gap-1">
                            {lead.phone && (
                              <div
                                className={`flex items-center gap-2 text-sm ${secondaryText} cursor-pointer`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleLogCall(lead);
                                }}
                              >
                                <PhoneLink
                                  phoneNumber={lead.phone}
                                  entityType="lead"
                                  entityId={lead.id}
                                  entityName={lead.fullName || lead.companyName}
                                />
                              </div>
                            )}
                            {lead.email && (
                              <div
                                className={`flex items-center gap-2 text-sm ${secondaryText}`}
                              >
                                <Mail size={14} className={mutedText} />
                                <a
                                  href={`mailto:${lead.email}`}
                                  className="hover:text-blue-400 hover:underline transition-colors"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {lead.email}
                                </a>
                              </div>
                            )}
                            {/* Disqualification reason shown inline for unqualified leads */}
                            {(lead.salesEngagementStep === "unqualified" ||
                              lead.leadStatus === "unqualified" ||
                              lead.leadStatus === "dead") &&
                              lead.disqualificationReason && (
                                <div className="flex items-start gap-1.5 mt-0.5">
                                  <XCircle
                                    size={13}
                                    className="text-red-400 mt-0.5 flex-shrink-0"
                                  />
                                  <span
                                    className={`text-xs leading-tight ${
                                      isDarkMode
                                        ? "text-red-400"
                                        : "text-red-500"
                                    }`}
                                  >
                                    {formatDisqualificationReason(
                                      lead.disqualificationReason,
                                    )}
                                  </span>
                                </div>
                              )}
                          </div>
                        </td>

                        <td className="px-4 sm:px-6 py-4 hidden md:table-cell">
                          {lead.companyName ? (
                            <div className="flex items-center gap-2">
                              <Building2 size={16} className={mutedText} />
                              <span className={primaryText}>
                                {lead.companyName}
                              </span>
                            </div>
                          ) : (
                            <span
                              className={
                                isDarkMode ? "text-gray-500" : "text-gray-400"
                              }
                            >
                              —
                            </span>
                          )}
                        </td>

                        <td className="px-4 sm:px-6 py-4 hidden lg:table-cell">
                          {lead.assignedUser ? (
                            <div className="flex items-center gap-2">
                              <div
                                className={`w-8 h-8 rounded-full flex items-center justify-center overflow-hidden flex-shrink-0 ${
                                  isDarkMode ? "bg-gray-700" : "bg-gray-200"
                                }`}
                              >
                                {lead.assignedUser.profileImageUrl ? (
                                  <img
                                    src={lead.assignedUser.profileImageUrl}
                                    alt=""
                                    className="w-full h-full object-cover"
                                  />
                                ) : (
                                  <span
                                    className={`text-sm font-medium ${
                                      isDarkMode
                                        ? "text-white"
                                        : "text-gray-700"
                                    }`}
                                  >
                                    {lead.assignedUser.name
                                      ?.charAt(0)
                                      .toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <span className={`text-sm ${primaryText}`}>
                                {lead.assignedUser.name}
                              </span>
                            </div>
                          ) : (
                            <span
                              className={`text-sm ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}
                            >
                              Unassigned
                            </span>
                          )}
                        </td>

                        <td className="px-4 sm:px-6 py-4 hidden sm:table-cell">
                          <div className="flex items-center gap-2">
                            <Phone size={14} className={mutedText} />
                            <span className={primaryText}>
                              {lead.callNoteCount || 0}
                            </span>
                          </div>
                        </td>

                        <td className="px-4 sm:px-6 py-4">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleLogCall(lead);
                              }}
                              className="p-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                              title="Log Call"
                            >
                              <Phone size={16} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/leads/${lead.id}`);
                              }}
                              className={`p-2 rounded-lg transition-colors text-white ${
                                isDarkMode
                                  ? "bg-gray-700 hover:bg-gray-600"
                                  : "bg-gray-200 hover:bg-gray-300 !text-gray-700"
                              }`}
                              title="View Details"
                            >
                              <Eye size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && pagination.pages > 1 && (
            <div
              className={`flex flex-col sm:flex-row items-center justify-between px-4 sm:px-6 py-4 gap-3 border-t ${
                isDarkMode ? "border-gray-700/50" : "border-gray-200"
              }`}
            >
              <div className={`text-sm ${mutedText}`}>
                Showing{" "}
                <span className={`font-medium ${primaryText}`}>
                  {(pagination.page - 1) * pagination.limit + 1}
                </span>{" "}
                to{" "}
                <span className={`font-medium ${primaryText}`}>
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  )}
                </span>{" "}
                of{" "}
                <span className={`font-medium ${primaryText}`}>
                  {pagination.total}
                </span>{" "}
                leads
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      page: Math.max(1, prev.page - 1),
                    }))
                  }
                  disabled={pagination.page === 1}
                  className={`flex items-center gap-1 px-3 py-2 border rounded-lg text-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${paginateBtnCls}`}
                >
                  <ChevronLeft size={16} />
                  Previous
                </button>

                <div className="flex items-center gap-1">
                  {Array.from(
                    { length: Math.min(5, pagination.pages) },
                    (_, i) => {
                      let pageNum;
                      if (pagination.pages <= 5) pageNum = i + 1;
                      else if (pagination.page <= 3) pageNum = i + 1;
                      else if (pagination.page >= pagination.pages - 2)
                        pageNum = pagination.pages - 4 + i;
                      else pageNum = pagination.page - 2 + i;
                      return (
                        <button
                          key={pageNum}
                          onClick={() =>
                            setPagination((prev) => ({
                              ...prev,
                              page: pageNum,
                            }))
                          }
                          className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors ${
                            pagination.page === pageNum
                              ? "bg-blue-600 text-white"
                              : isDarkMode
                                ? "bg-gray-800/50 border border-gray-700/50 text-gray-400 hover:bg-gray-700/50"
                                : "bg-white border border-gray-300 text-gray-500 hover:bg-gray-50"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    },
                  )}
                </div>

                <button
                  onClick={() =>
                    setPagination((prev) => ({
                      ...prev,
                      page: Math.min(prev.pages, prev.page + 1),
                    }))
                  }
                  disabled={pagination.page === pagination.pages}
                  className={`flex items-center gap-1 px-3 py-2 border rounded-lg text-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors ${paginateBtnCls}`}
                >
                  Next
                  <ChevronRight size={16} />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Log Call Modal */}
      {showLogCallModal && selectedLead && (
        <LogCallModal
          lead={selectedLead}
          onClose={() => {
            setShowLogCallModal(false);
            setSelectedLead(null);
          }}
          onSuccess={() => {
            setShowLogCallModal(false);
            setSelectedLead(null);
            fetchLeads();
            fetchStats();
          }}
        />
      )}

      {/* Advanced Filter Modal */}
      {showAdvancedFilters && (
        <AdvancedLeadFilterModal
          isOpen={showAdvancedFilters}
          onClose={() => setShowAdvancedFilters(false)}
          onApplyFilters={(filterConfig, presetId) => {
            setAdvancedFilterConfig(filterConfig);
            setSelectedFilterPresetId(presetId);
            hasRestoredScroll.current = false;
          }}
        />
      )}
    </div>
  );
};

export default SalesEngagement;
